import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Plus, Wallet, ArrowDownCircle, ArrowUpCircle, Scale, ArrowLeftRight, RotateCcw } from 'lucide-react'
import {
  useCashTransactions,
  useCashFlowSummary,
  useReverseCashTransaction,
} from '@/hooks/useCashTransactions'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { useCashCategories } from '@/hooks/useCashCategories'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { Button, buttonVariants } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { formatBRL } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { CashTransactionModal } from './CashTransactionModal'
import { TransferModal } from './TransferModal'
import type { CashTransaction, CashTransactionSource } from '@/types/cash'
import type { Column } from '@/components/ui/data-table'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

const SOURCE_LABELS: Record<CashTransactionSource, string> = {
  MANUAL: 'Manual',
  INVOICE_PAYMENT: 'Fatura',
  REPAYMENT_PAYMENT: 'Repasse',
  SALARY_PAYMENT: 'Salário',
  OPERATIONAL_EXPENSE: 'Despesa',
  OBLIGATION_SETTLED: 'Conta quitada',
  TRANSFER: 'Transferência',
  REVERSAL: 'Estorno',
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR')
}

interface SummaryCardProps {
  label: string
  value: string
  icon: React.ElementType
  tone: 'neutral' | 'green' | 'red'
}

function SummaryCard({ label, value, icon: Icon, tone }: SummaryCardProps) {
  const toneClass = {
    neutral: 'text-foreground',
    green: 'text-green-700',
    red: 'text-red-700',
  }[tone]
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={16} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className={cn('mt-2 text-xl font-semibold tabular-nums', toneClass)}>{value}</p>
    </div>
  )
}

const COLUMNS: Column<CashTransaction>[] = [
  { key: 'date', header: 'Data', render: (row) => formatDate(row.date) },
  { key: 'code', header: 'Código', render: (row) => `C-${row.code}` },
  { key: 'description', header: 'Descrição' },
  {
    key: 'category',
    header: 'Categoria',
    className: 'hidden lg:table-cell',
    render: (row) => row.category?.name ?? '—',
  },
  {
    key: 'account',
    header: 'Conta',
    className: 'hidden lg:table-cell',
    render: (row) => row.account.name,
  },
  {
    key: 'source',
    header: 'Origem',
    className: 'hidden lg:table-cell',
    render: (row) => (
      <span className="text-xs text-muted-foreground">{SOURCE_LABELS[row.source]}</span>
    ),
  },
  {
    key: 'amount',
    header: 'Valor',
    render: (row) => (
      <span
        className={cn(
          'font-medium tabular-nums',
          row.type === 'INFLOW' ? 'text-green-700' : 'text-red-700',
        )}
      >
        {row.type === 'INFLOW' ? '+' : '−'} {formatBRL(row.amount)}
      </span>
    ),
  },
]

export function CashFlowPage() {
  const [accountId, setAccountId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [type, setType] = useState<'' | 'INFLOW' | 'OUTFLOW'>('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [reverseTarget, setReverseTarget] = useState<CashTransaction | null>(null)
  const reverseMutation = useReverseCashTransaction()

  const filters = {
    ...(accountId && { accountId }),
    ...(categoryId && { categoryId }),
    ...(type && { type }),
    ...(startDate && { startDate: new Date(startDate).toISOString() }),
    ...(endDate && { endDate: new Date(`${endDate}T23:59:59`).toISOString() }),
  }

  const { data: summary } = useCashFlowSummary(filters)
  const { data, isLoading } = useCashTransactions({ ...filters, page })
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 })
  const { data: categoriesData } = useCashCategories({ active: true, perPage: 100 })

  const accounts = accountsData?.data ?? []
  const categories = categoriesData?.data ?? []

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v)
      setPage(1)
    }
  }

  async function handleReverse() {
    if (!reverseTarget) return
    try {
      await reverseMutation.mutateAsync({ id: reverseTarget.id })
      toast.success('Lançamento estornado')
      setReverseTarget(null)
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message
      toast.error(msg ?? 'Erro ao estornar lançamento')
    }
  }

  const columns: Column<CashTransaction>[] = [
    ...COLUMNS,
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.source === 'MANUAL' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReverseTarget(row)}
            title="Estornar lançamento"
          >
            <RotateCcw size={14} />
            Estornar
          </Button>
        ) : null,
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Fluxo de Caixa</h1>
          <p className="text-sm text-muted-foreground">
            Entradas, saídas e saldo das contas da empresa
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/cash/accounts" className={buttonVariants({ variant: 'outline' })}>
            Contas
          </Link>
          <Link to="/cash/categories" className={buttonVariants({ variant: 'outline' })}>
            Categorias
          </Link>
          <Button variant="outline" onClick={() => setTransferOpen(true)}>
            <ArrowLeftRight size={16} />
            Transferência
          </Button>
          <Button onClick={() => setModalOpen(true)}>
            <Plus size={16} />
            Novo lançamento
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="Saldo consolidado"
          value={formatBRL(summary?.consolidatedBalance ?? 0)}
          icon={Wallet}
          tone="neutral"
        />
        <SummaryCard
          label="Entradas no período"
          value={formatBRL(summary?.totalInflow ?? 0)}
          icon={ArrowUpCircle}
          tone="green"
        />
        <SummaryCard
          label="Saídas no período"
          value={formatBRL(summary?.totalOutflow ?? 0)}
          icon={ArrowDownCircle}
          tone="red"
        />
        <SummaryCard
          label="Resultado do período"
          value={formatBRL(summary?.net ?? 0)}
          icon={Scale}
          tone={Number.parseFloat(summary?.net ?? '0') >= 0 ? 'green' : 'red'}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <select
          className={selectClass}
          value={accountId}
          onChange={(e) => resetPage(setAccountId)(e.target.value)}
        >
          <option value="">Todas as contas</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={categoryId}
          onChange={(e) => resetPage(setCategoryId)(e.target.value)}
        >
          <option value="">Todas as categorias</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={type}
          onChange={(e) => resetPage(setType)(e.target.value as '' | 'INFLOW' | 'OUTFLOW')}
        >
          <option value="">Entradas e saídas</option>
          <option value="INFLOW">Somente entradas</option>
          <option value="OUTFLOW">Somente saídas</option>
        </select>
        <input
          type="date"
          className={selectClass}
          value={startDate}
          onChange={(e) => resetPage(setStartDate)(e.target.value)}
          aria-label="Data inicial"
        />
        <input
          type="date"
          className={selectClass}
          value={endDate}
          onChange={(e) => resetPage(setEndDate)(e.target.value)}
          aria-label="Data final"
        />
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="Nenhum lançamento encontrado"
        emptyDescription="Registre lançamentos ou ajuste os filtros."
        emptyAction={
          <Button onClick={() => setModalOpen(true)}>
            <Plus size={16} />
            Novo lançamento
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}

      <CashTransactionModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <TransferModal open={transferOpen} onClose={() => setTransferOpen(false)} />

      <ConfirmDialog
        open={!!reverseTarget}
        title="Estornar lançamento"
        description={
          reverseTarget
            ? `Será criado um lançamento oposto a "${reverseTarget.description}" (C-${reverseTarget.code}), anulando seu efeito no saldo. O lançamento original é preservado.`
            : ''
        }
        confirmLabel="Estornar"
        destructive
        loading={reverseMutation.isPending}
        onConfirm={handleReverse}
        onCancel={() => setReverseTarget(null)}
      />
    </div>
  )
}
